import { Component, AfterViewInit, Inject, HostListener } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular material components
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// Custom services 
import { UtilsService } from '../../../services/utils/utils.service';
import { LanguageService } from '../../../services/language/language.service';
import { DicomService } from '../../../services/dicom/dicom.service';

// Global variables
import { GlobalVariables } from '../../../constants/global';

/* Specify the structure of the dictionary that will receive the dialog when being openned */
export interface DialogData { urlImagePng:any, imageWidth:number, imageHeight:number, classMapBin:any }


@Component({
  selector: 'app-modify-popup',
  standalone: true,
  imports: [
    // Angular modules
    CommonModule, 
    // Material design modules
    MatMenuModule, 
    MatIconModule,
    MatButtonModule,
    MatSliderModule,
    FormsModule,
    MatSlideToggleModule,],
  templateUrl: './modify-popup.component.html',
  styleUrl: './modify-popup.component.css'
})
export class ModifyPopupComponent implements AfterViewInit {

  private _keysPressed:any = {};
  private _nChannels:number = 4

  unsavedChanges:boolean = false
  shouldUpdateClassMap:boolean = false

  classes = GlobalVariables.batscan.classes;
  activeClassId:number = GlobalVariables.batscan.classes[0].id;  // ID of the class that is currently selected.
  className: string = this.classes[0].name; // Name of the class that is currently selected.
  temporalClassMapBin: Uint8ClampedArray; // Save the temporal class map with 0, 1, 2, 3...
  currentClassHasBeenModified: boolean = false; // Flag to check if the current class has been modified.
  
  opacity:number = 0.5
  invOpacity:number = 1 - this.opacity
  zoom:number = 2
  maxZoom:number = 5
  cursorColor:any = GlobalVariables.batscan.cursorColor
  cursorSize:number = 20
  cursorShape:number = 0 // 0: circle, 1: square
  cursorX:number = null
  cursorY:number = null
  clickScrollJump:number = 10
  mouseDown:boolean = false
  mouseDownStartX = null
  mouseDownStartY = null
  mouseDragButton:number = null
  scrollPosX:number = 0
  scrollPosY:number = 0
  visualizationMinHU:number = GlobalVariables.batscan.visualizationMinHU
  visualizationMaxHU:number = GlobalVariables.batscan.visualizationMaxHU

  urlImagePng:any = null
  imageWidth:number = null
  imageHeight:number = null
  classMapBin:Uint8ClampedArray = null
  classMapBinBackup:any = null

  @ViewChild('canvasElement') canvasElement!: ElementRef;
  @ViewChild('canvasElement_Overlay') canvasElement_Overlay!: ElementRef;
  @ViewChild('canvasElement_Container') canvasElement_Container!: ElementRef;
  @ViewChild('canvasElement_InnerContainer') canvasElement_InnerContainer!: ElementRef;


  constructor(public utilsService:UtilsService,
    public dicomService:DicomService,
    public languageService:LanguageService,
    public dialogRef: MatDialogRef<ModifyPopupComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DialogData)
  {
    this.urlImagePng = data.urlImagePng
    this.classMapBin = data.classMapBin
    this.imageWidth = data.imageWidth
    this.imageHeight = data.imageHeight
    this.classMapBin = data.classMapBin;
    this.temporalClassMapBin = new Uint8ClampedArray(this.classMapBin.length);
    this.loadCurrentClassMapBin();
  }


  async ngAfterViewInit() {
    // Display image and class map
    this.displayImageAndClassMap()
    // Resize and center Canvas
    this.resizeAndCenterCanvas()
  }

  async displayImageAndClassMap() {
    const canvas = this.canvasElement.nativeElement
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const img = new Image();
  
    img.onload = () => {
      const scaledWidth = this.imageWidth * this.zoom;
      const scaledHeight = this.imageHeight * this.zoom;
  
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;
      canvas.style.width = `${scaledWidth}px`;
      canvas.style.height = `${scaledHeight}px`;
  
      ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);
  
      let imageData = ctx.getImageData(0, 0, scaledWidth, scaledHeight);
  
      for (let row = 0; row < scaledHeight; row++) {
        for (let col = 0; col < scaledWidth; col++) {
          let index = Math.floor(row / this.zoom) * this.imageWidth + Math.floor(col / this.zoom);
          let dataIndex = row * scaledWidth + col;
          
          let isActivePixel = this.temporalClassMapBin[index];
          
          if (isActivePixel === 1) {
            for (let channel = 0; channel < (this._nChannels - 1); channel++) {
              imageData.data[dataIndex * this._nChannels + channel] =
                imageData.data[dataIndex * this._nChannels + channel] * this.invOpacity +
                this.cursorColor[channel] * this.opacity;
            }
          }
          imageData.data[dataIndex * this._nChannels + 3] = 255;
        }
      }
      ctx.putImageData(imageData, 0, 0);
    };
    img.src = this.urlImagePng;
    img.crossOrigin = "anonymous";
  }
  
  updateOpacity(increment:number){
    this.opacity = Math.min(1, Math.max(0, this.opacity + increment))
    this.invOpacity = (1 - this.opacity)
    // Refresh image and class map
    this.displayImageAndClassMap()
  }

  updateZoom(increment:number){
    this.zoom = Math.max(1, Math.min(this.maxZoom, this.zoom + increment))
    // Refresh image and class map
    this.displayImageAndClassMap()
    // Resize and center Canvas
    this.resizeAndCenterCanvas()
  }

  updateCurrentClassIndex(increment: number) {
    let currentIndex = this.classes.findIndex(c => c.id === this.activeClassId);
    let newIndex = currentIndex + increment;
    if (newIndex >= 0 && newIndex < this.classes.length) {
      this.setActiveClass(this.classes[newIndex].id);
    }
  }

  updateCursorSize(increment:number){
    this.cursorSize = Math.max(5, Math.min(50, this.cursorSize + increment))
    // Display cursor
    this.drawCursor()
  }

  saveChanges(){
    this.classMapBinBackup = this.classMapBin;
    this.unsavedChanges = false
    this.shouldUpdateClassMap = true
  }

  shouldSaveBeforeExiting(shouldSave){
    if(shouldSave) this.saveChanges()
    this.dialogRef.close({
       shouldUpdateClassMap: this.shouldUpdateClassMap, 
       classMapBin: this.classMapBin,
       imageWidth: this.imageWidth, 
       imageHeight: this.imageHeight
    })
  }


  // ----- Global and Canvas actions ----- //


  global_onWheel(event){
    event.preventDefault()
    event.stopPropagation();
    if(this._keysPressed['Control']){
      if(event.deltaY > 0) this.updateZoom(-1)
      if(event.deltaY < 0) this.updateZoom(+1)
    } else if(this._keysPressed['c'] || this._keysPressed['C']){
      if(event.deltaY > 0) this.updateCurrentClassIndex(-1)
      if(event.deltaY < 0) this.updateCurrentClassIndex(+1)
    } else {
      if(event.deltaY > 0) this.updateOpacity(-0.1)
      if(event.deltaY < 0) this.updateOpacity(+0.1)
    }
  }

  canvas_onRightClick(event){
    event.preventDefault()
  }

  saveButtonClicked(){
    this.storeCurrentClassMapBin()
    this.saveChanges()
    this.utilsService.displaySnackBar(this.languageService.Strings.feedbackStrings.successfullySaved);
  }

  exitButtonClicked(){
    this.storeCurrentClassMapBin()
    // Manage unsaved changes
    if(!this.unsavedChanges){
      this.dialogRef.close({
       shouldUpdateClassMap: this.shouldUpdateClassMap, 
       classMapBin: this.classMapBin,
       imageWidth: this.imageWidth, 
       imageHeight: this.imageHeight
    })
    }else{
      // Saving confirmation popup (Decision popup)
      this.utilsService.displayDecisionPopup(
        this.languageService.Strings.popups.titleSaveChanges,
        this.languageService.Strings.popups.textSaveChanges,
        this.shouldSaveBeforeExiting.bind(this)
      )
    }
  }

  moveWithButtons(dx, dy){
    const canvasContainer = this.canvasElement_Container.nativeElement
    var maxScrollLeft = canvasContainer.scrollWidth - canvasContainer.clientWidth;
    var maxScrollTop = canvasContainer.scrollHeight - canvasContainer.clientHeight;
    this.scrollPosX = Math.max(0, Math.min(maxScrollLeft, this.scrollPosX - dx * this.clickScrollJump))
    this.scrollPosY = Math.max(0, Math.min(maxScrollTop, this.scrollPosY - dy * this.clickScrollJump ))
    canvasContainer.scrollTo({ left: this.scrollPosX, top: this.scrollPosY, behavior: 'auto' });
  }

  drawCursor() {
    const canvasOverlay = this.canvasElement_Overlay.nativeElement
    const ctx = canvasOverlay.getContext('2d', { willReadFrequently: true });
    let color = `rgb(${this.cursorColor[0]}, ${this.cursorColor[1]}, ${this.cursorColor[2]})`;
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.beginPath();
    if(this.cursorShape == 0){  // Circle
      ctx.arc(this.cursorX, this.cursorY, this.cursorSize, 0, 2 * Math.PI, false);
    } else if(this.cursorShape == 1){ // Square
      ctx.rect(this.cursorX - this.cursorSize, this.cursorY - this.cursorSize, this.cursorSize*2, this.cursorSize*2);
    } 
    ctx.strokeStyle = color;
    ctx.stroke();
  }

  async updateCursorShape(){
    if(this.cursorShape == 0) this.cursorShape = 1
    else if(this.cursorShape == 1) this.cursorShape = 0
    // Displaly cursor
    this.drawCursor()
  }

  // Methods to manage which keys are pressed
  @HostListener('document:keydown', ['$event'])
  keydown(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Store the current key
    this._keysPressed[event.key] = true;
    // Additional actions for specific keys
    switch (event.key) {
      case '+':
        this.updateCursorSize(+1)
        break;
      case '-':
        this.updateCursorSize(-1)
        break;
      case ' ':
        this.updateCursorShape()
        break;
      case 'Escape':
        this.exitButtonClicked();
        break;
      case 'z':
        // if(event.ctrlKey) this.updateLabelingState(-1)
        break;
      case 'Z':
        // if(event.ctrlKey) this.updateLabelingState(-1)
        break;
      case 'y':
        // if(event.ctrlKey) this.updateLabelingState(+1)
        break;
      case 'Y':
        // if(event.ctrlKey) this.updateLabelingState(+1)
        break;
      case 's':
      case 'S':
        if(event.ctrlKey) this.saveChanges()
        break;
      default:
        // Remaining keys
        break;
    } 
  }

  @HostListener('document:keyup', ['$event'])
  keyup(event: KeyboardEvent) {
    delete this._keysPressed[event.key]; 
  }

  resizeAndCenterCanvas(){
    const canvas = this.canvasElement.nativeElement
    const canvasOverlay = this.canvasElement_Overlay.nativeElement
    const canvasContainer = this.canvasElement_Container.nativeElement
    const canvasInnerContainer = this.canvasElement_InnerContainer.nativeElement

    let scaledWidth = this.imageWidth * this.zoom
    let scaledHeight = this.imageHeight * this.zoom
    let leftOffset = 0
    let topOffset = 0
    
    canvasOverlay.width = scaledWidth;  
    canvasOverlay.height = scaledHeight;
    canvasOverlay.style.width = `${scaledWidth}px`;  
    canvasOverlay.style.height = `${scaledHeight}px`;
  
    canvasInnerContainer.style.width = 'fit-content';
    canvasInnerContainer.style.height = 'fit-content';

    if (canvasContainer.offsetWidth > scaledWidth && canvasContainer.offsetHeight > scaledHeight) {
      canvasContainer.style.alignItems = 'center';
      canvasContainer.style.justifyContent = 'center';
      canvas.style.top = '50%';
      canvas.style.left = '50%';
      canvas.style.transform = 'translate(-50%, -50%)';
      canvasOverlay.style.top = '50%';
      canvasOverlay.style.left = '50%';
      canvasOverlay.style.transform = 'translate(-50%, -50%)';
      leftOffset = scaledWidth / 2
      topOffset = scaledHeight / 2
    }
    else if(canvasContainer.offsetWidth > scaledWidth){
      canvasContainer.style.alignItems = 'center';
      canvasContainer.style.justifyContent = 'flex-start';
      canvas.style.top = '0';
      canvas.style.left = '50%';
      canvas.style.transform = 'translate(-50%, 0)';
      canvasOverlay.style.top = '0';
      canvasOverlay.style.left = '50%';
      canvasOverlay.style.transform = 'translate(-50%, 0)';
      leftOffset = scaledWidth / 2
    }
    else if(canvasContainer.offsetHeight > scaledHeight){
      canvasContainer.style.alignItems = 'flex-start';
      canvasContainer.style.justifyContent = 'center';
      canvas.style.top = '50%';
      canvas.style.left = '0';
      canvas.style.transform = 'translate(0, -50%)';
      canvasOverlay.style.top = '50%';
      canvasOverlay.style.left = '0';
      canvasOverlay.style.transform = 'translate(0, -50%)';
      topOffset = scaledHeight / 2
    }
    else{
      canvasContainer.style.alignItems = 'flex-start';
      canvasContainer.style.justifyContent = 'flex-start';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.transform = 'translate(0, 0)';
      canvasOverlay.style.top = '0';
      canvasOverlay.style.left = '0';
      canvasOverlay.style.transform = 'translate(0, 0)';
    }
    // Scroll to the center position
    var maxScrollLeft = canvasContainer.scrollWidth - canvasContainer.clientWidth;
    var maxScrollTop = canvasContainer.scrollHeight - canvasContainer.clientHeight;
    this.scrollPosX = Math.round(maxScrollLeft / 2)
    this.scrollPosY = Math.round(maxScrollTop / 2)
    canvasContainer.scrollTo({ left: this.scrollPosX, top: this.scrollPosY, behavior: 'auto' })

    // Display cursor
    canvasOverlay.addEventListener('mousemove', (event) => {
      this.cursorX = event.clientX - canvasInnerContainer.offsetLeft + leftOffset + this.scrollPosX
      this.cursorY = event.clientY - canvasInnerContainer.offsetTop + topOffset + this.scrollPosY
        this.drawCursor();
    });
    // Set mouse events
    this.setMouseEvents()
  }

   setMouseEvents(){
    // Add mouse event listener
    const canvasOverlay = this.canvasElement_Overlay.nativeElement
    canvasOverlay.addEventListener('mousedown', (event) => {
      this.mouseDown = true;
      this.mouseDownStartX = event.clientX;
      this.mouseDownStartY = event.clientY;
      if(!this._keysPressed['Control'] && event.button == 0){
        this.mouseDragButton = 0
        this.label()
      } else if(!this._keysPressed['Control'] && event.button == 2){
        this.mouseDragButton = 2
        this.unlabel()
      }
    });

    document.addEventListener('mousemove', (event) => {
      const canvasContainer = this.canvasElement_Container.nativeElement
      if (this.mouseDown && this._keysPressed['Control']) {
        // Drag moving
        let dx = event.clientX - this.mouseDownStartX;
        let dy = event.clientY - this.mouseDownStartY;
        // Scroll
        var maxScrollLeft = canvasContainer.scrollWidth - canvasContainer.clientWidth;
        var maxScrollTop = canvasContainer.scrollHeight - canvasContainer.clientHeight;
        this.scrollPosX = Math.max(0, Math.min(maxScrollLeft, this.scrollPosX - dx ))
        this.scrollPosY = Math.max(0, Math.min(maxScrollTop, this.scrollPosY - dy ))
        canvasContainer.scrollTo({ left: this.scrollPosX, top: this.scrollPosY, behavior: 'auto' });
        // Update variables
        this.mouseDownStartX = event.clientX;
        this.mouseDownStartY = event.clientY;
      } else if (this.mouseDown && !this._keysPressed['Control'] && this.mouseDragButton == 0){ 
        this.label();
      } else if(this.mouseDown && !this._keysPressed['Control'] && this.mouseDragButton == 2){ 
        this.unlabel(); 
      }
    });
    
    document.addEventListener('mouseup', (event) => {
      this.mouseDown = false;
    });
  }

  async label(){
    // Carry out the labeling actions
    let midCol = this.cursorX / this.zoom
    let midRow = this.cursorY / this.zoom
    let xStart = Math.max(0, Math.round((this.cursorX - this.cursorSize)/this.zoom))
    let xEnd = Math.min((this.imageWidth - 1), Math.round((this.cursorX + this.cursorSize)/this.zoom))
    let yStart = Math.max(0, Math.round((this.cursorY - this.cursorSize)/this.zoom))
    let yEnd = Math.min((this.imageHeight - 1), Math.round((this.cursorY + this.cursorSize)/this.zoom))
    for(let col = xStart; col < xEnd; col++){
      for(let row = yStart; row < yEnd; row++){
        if( (this.cursorShape == 0) && ((col-midCol)**2 + (row-midRow)**2 > (this.cursorSize/this.zoom)**2) ) continue
        let index = (row * this.imageWidth) + col   
        this.temporalClassMapBin[index] = 1; 
        this.currentClassHasBeenModified = true; 
        this.unsavedChanges = true;
      }
    }
    this.displayImageAndClassMap()
  }

  async unlabel(){
    // Carry out the unlabeling actions
    let midCol = this.cursorX / this.zoom
    let midRow = this.cursorY / this.zoom
    let xStart = Math.max(0, Math.round((this.cursorX - this.cursorSize)/this.zoom))
    let xEnd = Math.min((this.imageWidth - 1), Math.round((this.cursorX + this.cursorSize)/this.zoom))
    let yStart = Math.max(0, Math.round((this.cursorY - this.cursorSize)/this.zoom))
    let yEnd = Math.min((this.imageHeight - 1), Math.round((this.cursorY + this.cursorSize)/this.zoom))
    for(let col = xStart; col < xEnd; col++){
      for(let row = yStart; row < yEnd; row++){
        if( (this.cursorShape == 0) && ((col-midCol)**2 + (row-midRow)**2 > (this.cursorSize/this.zoom)**2) ) continue
        let index = (row * this.imageWidth) + col
        this.temporalClassMapBin[index] = 0; 
        this.currentClassHasBeenModified = true; 
        this.unsavedChanges = true;
      }
    }
    this.displayImageAndClassMap()
  }

  loadCurrentClassMapBin() {
    for(let index = 0; index < this.classMapBin.length; index++) {
      this.temporalClassMapBin[index] = (this.classMapBin[index] === this.activeClassId) ? 1 : 0;
    }
    this.currentClassHasBeenModified = false; 
  }

  storeCurrentClassMapBin() {
    if (!this.currentClassHasBeenModified) return; 

    for(let index = 0; index < this.classMapBin.length; index++) {
      if (this.temporalClassMapBin[index] === 1) {
        this.classMapBin[index] = this.activeClassId;
      } else if (this.temporalClassMapBin[index] === 0 && this.classMapBin[index] === this.activeClassId) {
        this.classMapBin[index] = 0;
      }
    }
  }

  setActiveClass(id: number) {
    if (this.classMapBin) this.storeCurrentClassMapBin();
    
    this.activeClassId = id;
    
    const selectedClass = this.classes.find(c => c.id === id);
    if (selectedClass) {
      this.className = selectedClass.name; 
      this.cursorColor = selectedClass.color; 
      
      if (this.classMapBin) {
          this.loadCurrentClassMapBin();
          this.displayImageAndClassMap();
      }
      
      if (this.canvasElement_Overlay?.nativeElement && this.cursorX !== null && this.cursorY !== null) {
        this.drawCursor(); 
      }
    }
  }
}
